import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Page from "../../components/Page";
import { useLoyaltySettings, saveLoyaltySettings } from "../../controllers/loyalty.controller";
import { useMenuItems } from "../../controllers/menu_item.controller";
import { toast } from "react-hot-toast";
import { mutate } from "swr";
import Popover from "../../components/Popover";

export default function LoyaltySettingsPage() {
  const { t } = useTranslation();
  const { APIURL, data, error, isLoading } = useLoyaltySettings();
  const { data: menuItemsData } = useMenuItems({ status: "active" });

  const [isEnabled, setIsEnabled] = useState(false);
  const [rewardType, setRewardType] = useState("points");

  const rewardItemIdRef = useRef();
  const thresholdCountRef = useRef();
  const pointsPercentageRef = useRef();
  const pointValueRef = useRef();

  useEffect(() => {
    if (data) {
      setIsEnabled(data.is_enabled);
      setRewardType(data.type);
    }
  }, [data]);

  if (isLoading) {
    return <Page className="px-8 py-6">{t("settings.please_wait")}</Page>;
  }

  const btnSave = async () => {
    const settings = {
      is_enabled: isEnabled,
      type: rewardType,
      reward_item_id: rewardItemIdRef.current?.value || null,
      threshold_count: thresholdCountRef.current?.value || 10,
      points_percentage: pointsPercentageRef.current?.value || 1,
      point_value: pointValueRef.current?.value || 1,
    };

    try {
      toast.loading(t("settings.please_wait"));
      const res = await saveLoyaltySettings(settings);

      if (res.status === 200) {
        await mutate(APIURL);
        toast.dismiss();
        toast.success(res.data.message);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t("settings.something_went_wrong");
      toast.dismiss();
      toast.error(message);
    }
  };

  return (
    <Page className="px-8 py-6">
      <h3 className="text-3xl font-light">Loyalty Program</h3>

      <div className="mt-8 text-sm text-gray-500 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <label htmlFor="loyalty-enabled" className="text-lg text-restro-text font-medium">
            Enable Loyalty Program
          </label>
          <label className="relative inline-flex items-center cursor-pointer no-drag">
            <input
              type="checkbox"
              id="loyalty-enabled"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full  after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:border-restro-bg-gray after:border after:rounded-full after:h-5 after:w-5 after:transition-all bg-restro-checkbox peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-restro-ring-light peer-checked:bg-restro-green peer-checked:after:border-restro-border-green`}></div>
          </label>
        </div>

        {isEnabled && (
          <div className="space-y-6">
            <div>
              <label className="block mb-2 font-medium text-restro-text">Reward Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rewardType"
                    value="points"
                    checked={rewardType === "points"}
                    onChange={(e) => setRewardType(e.target.value)}
                    className="radio radio-primary radio-sm"
                  />
                  <span>Points-based (Cashback)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rewardType"
                    value="item"
                    checked={rewardType === "item"}
                    onChange={(e) => setRewardType(e.target.value)}
                    className="radio radio-primary radio-sm"
                  />
                  <span>Item-based (Buy X Get 1 Free)</span>
                </label>
              </div>
            </div>

            {rewardType === "points" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1">Cashback Percentage (%)</label>
                  <input
                    ref={pointsPercentageRef}
                    type="number"
                    defaultValue={data?.points_percentage || 1}
                    className="input w-full bg-restro-gray border-restro-border-green"
                    placeholder="e.g. 5 for 5%"
                  />
                  <p className="text-xs mt-1 text-gray-400">Points earned per 100 currency units.</p>
                </div>
                <div>
                  <label className="block mb-1">Point Redemption Value</label>
                  <input
                    ref={pointValueRef}
                    type="number"
                    defaultValue={data?.point_value || 1}
                    className="input w-full bg-restro-gray border-restro-border-green"
                    placeholder="e.g. 10 for 100 points = $10"
                  />
                  <p className="text-xs mt-1 text-gray-400">Value of 100 points when redeemed.</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1">Reward Item</label>
                  <select
                    ref={rewardItemIdRef}
                    defaultValue={data?.reward_item_id || ""}
                    className="select w-full bg-restro-gray border-restro-border-green"
                  >
                    <option value="" disabled>Select Item</option>
                    {menuItemsData?.map((item) => (
                      <option key={item.id} value={item.id}>{item.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1">Threshold (Buy X items)</label>
                  <input
                    ref={thresholdCountRef}
                    type="number"
                    defaultValue={data?.threshold_count || 10}
                    className="input w-full bg-restro-gray border-restro-border-green"
                    placeholder="e.g. 10"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={btnSave}
          className="text-white w-full lg:min-w-96 transition active:scale-95 rounded-xl px-4 py-2 mt-8 bg-restro-green hover:bg-restro-green-button-hover"
        >
          {t("settings.save")}
        </button>
      </div>
    </Page>
  );
}
